import { gql } from "@apollo/client";

export const GET_RISK_LIBRARY = gql`
  query GetRiskLibrary($filter: RiskFilterInput, $page: Int, $limit: Int) {
    getRiskLibrary(filter: $filter, page: $page, limit: $limit) {
      success
      message
      data {
        id
        title
        description
        key
        risk_category_id
        risk_category_title
        inherent_impact
        inherent_likelihood
        risk_owner_email
        is_active
      }
      totalCount
      currentPage
      totalPages
    }
  }
`;

export const ADD_RISKS_FROM_LIBRARY = gql`
  mutation AddRisksFromLibrary(
    $risk_keys: [String!]!
    $risk_owner_email: String!
  ) {
    addRisksFromLibrary(
      risk_keys: $risk_keys
      risk_owner_email: $risk_owner_email
    ) {
      success
      message
    }
  }
`;

export const GET_RISKS_FROM_REGISTER = gql`
  query GetRiskList($filter: RiskFilterInput, $page: Int, $limit: Int) {
    getRisks(filter: $filter, page: $page, limit: $limit) {
      success
      message
      data {
        id
        title
        description
        key
        risk_category_title
        risk_source_title
        risk_owner_email
        inherent_impact
        inherent_likelihood
        inherent_risk_score
        calculated_residual_risk_score
        is_active
        controls_count
        total_checks
        passed_checks
        created_at
        updated_at
      }
      totalCount
      currentPage
      totalPages
    }
  }
`;

export const ADD_MANUAL_RISK = gql`
  mutation AddManualRisk(
    $title: String!
    $description: String
    $risk_owner_email: String
    $risk_category_id: Int!
    $inherent_impact: Int!
    $inherent_likelihood: Int!
    $check_control_mappings: [RiskCheckControlMappingInput!]!
  ) {
    addRiskManually(
      title: $title
      description: $description
      risk_owner_email: $risk_owner_email
      risk_category_id: $risk_category_id
      inherent_impact: $inherent_impact
      inherent_likelihood: $inherent_likelihood
      check_control_mappings: $check_control_mappings
    ) {
      success
      message
      data {
        id
        title
        description
        key
        risk_category_title
        inherent_impact
        inherent_likelihood
        is_active
      }
    }
  }
`;

export const GET_RISK_BY_ID = gql`
  query GetRiskById($id: Int!) {
    getRiskById(id: $id) {
      success
      message
      data {
        id
        title
        description
        key
        risk_category_title
        risk_source_title
        inherent_impact
        inherent_likelihood
        inherent_risk_score
        calculated_residual_risk_score
        risk_owner_email
        is_active
        controls_count
        total_checks
        passed_checks
        created_at
        updated_at
      }
    }
  }
`;

export const UPDATE_RISK = gql`
  mutation UpdateRisk($risk_id: Int!, $update_data: RiskUpdateInput!) {
    updateRisk(risk_id: $risk_id, update_data: $update_data) {
      success
      message
      data {
        id
        title
        inherent_impact
        inherent_likelihood
        risk_owner_email
      }
    }
  }
`;

export const ARCHIVE_RISK = gql`
  mutation DeleteRisk($id: Int!) {
    deleteRisk(id: $id) {
      success
      message
      id
    }
  }
`;

export const UNARCHIVE_RISK = gql`
  mutation UnarchiveRisk($id: Int!) {
    unarchiveRisk(id: $id) {
      success
      message
      data {
        id
      }
    }
  }
`;

export const GET_CONTROLS_WITH_CHECKS_BY_RISK_ID = gql`
  query GetControlsWithChecksByRiskId($risk_id: Int!) {
    getControlsWithChecksByRiskId(risk_id: $risk_id) {
      success
      message
      data {
        controls_id
        controls_name
        controls_description
        control_domain
        control_title
        associated_checks {
          active_check_id
          active_check_name
          active_check_description
          check_type
          monitor_type
          active_check_category
          check_status
          check_status_reason
        }
      }
    }
  }
`;

export const START_ASSESSMENT = gql`
  mutation StartRiskAssessment($assessment_data: RiskAssessmentInput!) {
    startRiskAssessment(assessment_data: $assessment_data) {
      success
      message
      data {
        id
        title
        description
        status_id
        status_title
        assessor_email
        created_at
        updated_at
        completed_at
      }
    }
  }
`;

export const DELETE_DRAFT_ASSESSMENT = gql`
  mutation DeleteDraftAssessment($id: Int!) {
    deleteDraftAssessment(id: $id) {
      success
      message
      data {
        id
      }
    }
  }
`;

export const UPDATE_RISK_ASSESSMENT_ITEM = gql`
  mutation UpdateRiskAssessmentItem(
    $assessment_item_id: Int!
    $update_data: UpdateRiskAssessmentItemInput!
  ) {
    updateRiskAssessmentItem(
      assessment_item_id: $assessment_item_id
      update_data: $update_data
    ) {
      success
      message
      data {
        id
        treatment_id
        residual_risk_score
        comments
      }
    }
  }
`;

export const COMPLETE_RISK_ASSESSMENT = gql`
  mutation CompleteRiskAssessment($id: Int!) {
    completeRiskAssessment(id: $id) {
      success
      message
      data {
        id
        title
        status_id
        completed_at
      }
    }
  }
`;

export const GET_ALL_RISK_ASSESSMENTS = gql`
  query GetAllRiskAssessments($start_date: String, $end_date: String) {
    getAllRiskAssessments(start_date: $start_date, end_date: $end_date) {
      success
      message
      data {
        id
        title
        description
        assessor_email
        status_id
        status_title
        created_at
        updated_at
        completed_at
        approver_email
      }
    }
  }
`;

export const GET_AUDIT_RISK_ASSESSMENTS = gql`
  query GetAuditRiskAssessments(
    $audit_id: ID!
    $start_date: String
    $end_date: String
  ) {
    getAuditRiskAssessments(
      audit_id: $audit_id
      start_date: $start_date
      end_date: $end_date
    ) {
      success
      message
      data {
        id
        title
        description
        assessor_email
        status_id
        status_title
        created_at
        updated_at
        completed_at
        approver_email
      }
    }
  }
`;

export const GET_RISK_ASSESSMENT_ITEMS_BY_ID = gql`
  query GetRiskAssessmentItems($assessment_id: Int!) {
    getRiskAssessmentItemsById(assessment_id: $assessment_id) {
      success
      message
      data {
        id
        assessment_id
        original_risk_id
        risk_title
        risk_description
        risk_key
        risk_owner_email
        risk_inherent_impact
        risk_inherent_likelihood
        inherent_risk_score
        residual_risk_score
        risk_category_id
        treatment_id
        comments
        risk_category_title
        risk_source_title
        treatment_title
        created_at
        updated_at
        controls_count
      }
    }
  }
`;

export const GET_AUDIT_RISK_ASSESSMENT_ITEMS_BY_ID = gql`
  query GetAuditRiskAssessmentItems($audit_id: ID!, $assessment_id: Int!) {
    getAuditRiskAssessmentItemsById(
      audit_id: $audit_id
      assessment_id: $assessment_id
    ) {
      success
      message
      data {
        id
        assessment_id
        original_risk_id
        risk_title
        risk_description
        risk_key
        risk_owner_email
        risk_inherent_impact
        risk_inherent_likelihood
        inherent_risk_score
        residual_risk_score
        risk_category_id
        treatment_id
        comments
        risk_category_title
        risk_source_title
        treatment_title
        created_at
        updated_at
        controls_count
      }
    }
  }
`;

export const GET_CONTROLS_WITH_CHECKS_BY_RISK_ASSESSMENT_ID = gql`
  query getControlsWithChecksByRiskAssessmentId(
    $risk_id: Int!
    $assessment_id: Int!
  ) {
    getControlsWithChecksByRiskAssessmentId(
      risk_id: $risk_id
      assessment_id: $assessment_id
    ) {
      success
      message
      data {
        controls_id
        controls_name
        controls_description
        control_domain
        control_title
        associated_checks {
          active_check_id
          active_check_name
          active_check_description
          check_type
          monitor_type
          active_check_category
          check_status
          check_status_reason
        }
      }
    }
  }
`;
